import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';

import { useAuth } from '@/features/auth/hooks/useAuth';
import { createReportsApi } from '@/features/reports/api/reportsApi';
import { DEFAULT_FILTERS, PRESETS, matchPreset, toIsoDate } from '@/features/reports/lib/filters';

/**
 * Reports API bound to the caller's active role. Both admin roles see the same
 * report shapes; the prefix is what decides whose data comes back.
 */
export const useReportsApi = () => {
    const { activeRole } = useAuth();
    return useMemo(() => createReportsApi(activeRole), [activeRole]);
};

/**
 * Report filters live in the URL, so a filtered view can be shared, bookmarked
 * and reloaded without losing context.
 */
export const useReportFilters = () => {
    const [searchParams, setSearchParams] = useSearchParams();

    const filters = useMemo(
        () => ({
            from: searchParams.get('from') || DEFAULT_FILTERS.from,
            to: searchParams.get('to') || DEFAULT_FILTERS.to,
            granularity: searchParams.get('g') || DEFAULT_FILTERS.granularity,
            calendar: 'jalali',
            status: searchParams.get('status') ?? DEFAULT_FILTERS.status,
        }),
        [searchParams]
    );

    const update = useCallback(
        (patch) => {
            setSearchParams(
                (current) => {
                    const next = new URLSearchParams(current);
                    next.delete('cal');
                    const merged = { ...filters, ...patch };
                    // Only keep non-default values so the URL stays readable.
                    const write = (key, value, fallback) => {
                        if (value === undefined || value === null || value === '' || value === fallback) {
                            next.delete(key);
                        } else {
                            next.set(key, value);
                        }
                    };
                    write('from', merged.from, null);
                    write('to', merged.to, null);
                    write('g', merged.granularity, DEFAULT_FILTERS.granularity);
                    write('status', merged.status, '');
                    return next;
                },
                { replace: true }
            );
        },
        [filters, setSearchParams]
    );

    const applyPreset = useCallback(
        (presetKey) => {
            const preset = PRESETS.find((item) => item.key === presetKey);
            if (!preset) return;
            const range = preset.resolve();
            update({ from: range.from, to: range.to });
        },
        [update]
    );

    const reset = useCallback(() => {
        setSearchParams(new URLSearchParams(), { replace: true });
    }, [setSearchParams]);

    /** The exact query object every report endpoint accepts. */
    const query = useMemo(
        () => ({
            date_from: filters.from || undefined,
            date_to: filters.to || undefined,
            granularity: filters.granularity,
            calendar: 'jalali',
            status: filters.status || undefined,
        }),
        [filters]
    );

    return {
        filters,
        query,
        update,
        applyPreset,
        reset,
        activePreset: matchPreset(filters.from, filters.to),
        /** Caption used in page headers; null means the backend defaulted it. */
        hasExplicitRange: Boolean(filters.from && filters.to),
    };
};

/**
 * One shared query wrapper so every report gets identical caching and the
 * report name plus the filter set form a stable cache key.
 */
const useReportQuery = (name, fetcher, query, options = {}) =>
    useQuery({
        queryKey: ['reports', name, query],
        queryFn: () => fetcher(query),
        staleTime: 60_000,
        placeholderData: (previous) => previous,
        ...options,
    });

export const useSalesSummary = (query) => {
    const api = useReportsApi();
    return useReportQuery('sales-summary', api.salesSummary, query);
};

export const useSalesTimeseries = (query) => {
    const api = useReportsApi();
    return useReportQuery('sales-timeseries', api.salesTimeseries, query);
};

export const useSalesByWeekday = (query) => {
    const api = useReportsApi();
    return useReportQuery('sales-weekday', api.salesByWeekday, query);
};

export const useSalesByHour = (query) => {
    const api = useReportsApi();
    return useReportQuery('sales-hour', api.salesByHour, query);
};

export const useSalesByMonth = (query) => {
    const api = useReportsApi();
    return useReportQuery('sales-month', api.salesByMonth, query);
};

export const useTopProducts = (query, { limit = 20, orderBy = 'revenue' } = {}) => {
    const api = useReportsApi();
    const scoped = useMemo(
        () => ({ ...query, limit, order_by: orderBy }),
        [query, limit, orderBy]
    );
    return useReportQuery('top-products', api.topProducts, scoped);
};

export const useInventoryReport = (
    query,
    { limit = 100, onlyAttention = false, lowStockThreshold } = {}
) => {
    const api = useReportsApi();
    const scoped = useMemo(
        () => ({
            ...query,
            limit,
            only_attention: onlyAttention,
            low_stock_threshold: lowStockThreshold,
        }),
        [query, limit, onlyAttention, lowStockThreshold]
    );
    return useReportQuery('inventory', api.inventory, scoped);
};

export const useCategoryPerformance = (query, { limit = 100 } = {}) => {
    const api = useReportsApi();
    const scoped = useMemo(() => ({ ...query, limit }), [query, limit]);
    return useReportQuery('categories', api.categoryPerformance, scoped);
};

export const useOrderFunnel = (query) => {
    const api = useReportsApi();
    return useReportQuery('order-funnel', api.orderFunnel, query);
};

export const useFinanceOverview = (query) => {
    const api = useReportsApi();
    return useReportQuery('finance', api.financeOverview, query);
};

export const useCustomerCohorts = (query) => {
    const api = useReportsApi();
    return useReportQuery('customer-cohorts', api.customerCohorts, query);
};

export const useTopCustomers = (query, { limit = 20 } = {}) => {
    const api = useReportsApi();
    const scoped = useMemo(() => ({ ...query, limit }), [query, limit]);
    return useReportQuery('top-customers', api.topCustomers, scoped);
};

export const useVendorPerformance = (query, { limit = 50 } = {}) => {
    const api = useReportsApi();
    const scoped = useMemo(() => ({ ...query, limit }), [query, limit]);
    return useReportQuery('vendors', api.vendorPerformance, scoped);
};

export { toIsoDate };
